import { useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux";
import { menus } from "../configure/sideDrawerMenus";
import { setPrevRoleId } from "../redux/slices/UserDetails";
import { useOnlineStatus } from "@utils/UseOnlineStatus";

const findFirstAccessibleMenuRoute = (menus, rolePermissions) => {
  for (const menu of menus) {
    if (menu.id === 402 && !menu.requiredPermissions?.every((perm) => rolePermissions.includes(perm)) && menu.requiredPermissions?.some((perm) => rolePermissions.includes(perm)) && rolePermissions.includes("rm_employee_profile_view")){
      const employeeid = localStorage.getItem("employeeId")
      return `/rm/peoplelanding/profile/${employeeid}`
    }
    if (
      menu.requiredPermissions?.some((perm) => rolePermissions.includes(perm)) &&
      menu.to
    ) {
      return menu.to;
    }
    if (Array.isArray(menu.items)) {
      const child = findFirstAccessibleMenuRoute(menu.items, rolePermissions);
      if (child) return child;
    }
  }
  return null;
};

const RouteGuard = ({ Permissions = [], children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isOnline = useOnlineStatus();
  
  const isAuthenticated = useSelector((state) => state.currentUser?.isAuthenticated);
  const rolePermissions = useSelector((state) => state.permission?.applicationPermission || []);
  const roleId = useSelector((state) => state.currentUser?.currentUser?.roleId);
  const prevRoleId = useSelector((state) => state.currentUser?.prevRoleId);
  
  const firstAccessibleMenuRoute = useMemo(
    () => findFirstAccessibleMenuRoute(menus, rolePermissions) || "/permission-denied",
    [rolePermissions]
  );
  
  const isAuthorized = useMemo(() => {
    return Permissions.some((perm) => rolePermissions.includes(perm));
  }, [Permissions, rolePermissions]);
  
  const justChangedRole = useRef(false);
  
  // Detect and flag role change
  useEffect(() => {
    if (prevRoleId !== roleId) {
      justChangedRole.current = true;
      dispatch(setPrevRoleId(roleId));
    }
  }, [roleId, prevRoleId, dispatch]);

  // After role change, allow one redirect and reset
  useEffect(() => {
    if (justChangedRole.current) {
      const timeout = setTimeout(() => {
        justChangedRole.current = false;
      }, 100); // slight delay to reset safely after re-render
      return () => clearTimeout(timeout);
    }
  }, [roleId]);

  if (!isOnline) {
    return <Navigate to={location.pathname } replace />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if(sessionStorage.getItem("postLoginRedirect")){
    const route = sessionStorage.getItem("postLoginRedirect");
    sessionStorage.removeItem("postLoginRedirect");
    return <Navigate to={route} replace/>
  } else if (!isAuthorized  && justChangedRole.current && location.pathname !== firstAccessibleMenuRoute) {
    return <Navigate to={firstAccessibleMenuRoute} replace />;
  }

  if (!isAuthorized && !justChangedRole.current) {
    return <Navigate to="/permission-denied" replace state={{ from: location.pathname }} />;
  }

  return children;
};

export default RouteGuard;